import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  useProStudioCtxSelector,
  useProStudioStateMatches,
  sendProStudioEvent,
} from '../../../xstate/app.xstate';
import { ProStudioContext } from '../../../xstate/prostudio.xstate.types';
import { sendUserMessage } from '../../../xstate/app.xstate-utils';
import { tableStore } from '../../../stores/table-store';
import { useBobcatFlag } from '../../../hooks/use-bobcat-flag';
import { postSolutionVersionFiles } from './post-solution-version-files';

interface TaskType {
  name: string;
  description?: string;
  taskId?: string; // Optional taskId for existing tasks
}

interface DraftFileType {
  id?: string;
  url?: string;
  mimeType?: string;
  originalName?: string;
  publicId?: string;
  size?: number;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File | null;
  serverId?: string; // ID from server for existing files
}

interface FormSupervisorConfigType {
  supervisorName?: string;
  tone?: string;
  otherInformation?: string;
  temperature?: number;
  maxOutputTokens?: number;
  topP?: number;
  topK?: number;
  llmCredentialId?: string;
}

interface FormSupervisorTasksType {
  tasks: TaskType[];
}

interface FormSupervisorAgentType {
  brandName?: string;
  tone?: string;
  /** Round-trips through `SolutionVersionInput.knowledgeAgentContext` — see
   *  `tab-agent.tsx`'s `KnowledgeAgentContextField` doc comment. */
  knowledgeAgentContext?: string;
}

export const useSupervisorForm = () => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { draftVersionSolutionData } = useProStudioCtxSelector(
    (ctx: ProStudioContext) => ({
      draftVersionSolutionData: ctx.draftVersionSolutionData,
    })
  );

  // `'pending'` (flags not loaded yet) is not `'allowed'`, so it keeps the
  // global instructions out of the saved version.
  const globalInstructionsOn = useBobcatFlag('fiveStageNav') === 'allowed';

  const isUpdatingSolution = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingSolutionFlow'
  );

  useEffect(() => {
    setIsLoading(isUpdatingSolution);
  }, [isUpdatingSolution]);

  const getDefaultValues = useCallback(() => {
    if (!draftVersionSolutionData) {
      return {
        config: {
          supervisorName: '',
          otherInformation: '',
          temperature: 0.2,
          maxOutputTokens: 2048,
          topP: 0.8,
          topK: 0.2,
          llmCredentialId: undefined,
        },
        tasks: {
          tasks: [],
        },
        agent: {
          brandName: '',
          tone: '',
          knowledgeAgentContext: '',
        },
      };
    }

    return {
      config: {
        supervisorName: draftVersionSolutionData.supervisorName || '',
        otherInformation: draftVersionSolutionData.otherInformation || '',
        temperature: draftVersionSolutionData.temperature || 0.2,
        maxOutputTokens: draftVersionSolutionData.maxOutputTokens || 2048,
        topP: draftVersionSolutionData.topP || 0.8,
        topK: draftVersionSolutionData.topK || 0.2,
        llmCredentialId: draftVersionSolutionData.llmCredential?.id,
      },
      tasks: {
        tasks:
          draftVersionSolutionData.tasks?.map((task) => ({
            name: task.name || '',
            description: task.description || '',
            taskId: task.id || undefined, // Use taskId for existing tasks
          })) || [],
      },
      agent: {
        brandName: draftVersionSolutionData.brandName || '',
        tone: draftVersionSolutionData.tone || '',
        knowledgeAgentContext:
          draftVersionSolutionData.knowledgeAgentContext || '',
      },
    };
  }, [draftVersionSolutionData]);

  const convertExistingFiles = useCallback(() => {
    if (!draftVersionSolutionData?.files) {
      return [];
    }

    return draftVersionSolutionData.files.map((file: DraftFileType) => ({
      id: file.id || Math.random().toString(36).substr(2, 9),
      name: file.originalName || 'Unknown file',
      size: file.size || 0,
      type: file.mimeType || 'application/octet-stream',
      file: null,
      serverId: file.id, // Keep the server ID for existing files
    }));
  }, [draftVersionSolutionData]);

  const defaultValues = getDefaultValues();

  const configForm = useForm<FormSupervisorConfigType>({
    defaultValues: defaultValues.config,
  });

  const tasksForm = useForm<FormSupervisorTasksType>({
    defaultValues: defaultValues.tasks,
  });

  const agentForm = useForm<FormSupervisorAgentType>({
    defaultValues: defaultValues.agent,
  });

  useEffect(() => {
    const newDefaultValues = getDefaultValues();
    configForm.reset(newDefaultValues.config);
    tasksForm.reset(newDefaultValues.tasks);
    agentForm.reset(newDefaultValues.agent);

    const existingFiles = convertExistingFiles();
    setUploadedFiles(existingFiles);
  }, [
    draftVersionSolutionData,
    getDefaultValues,
    configForm,
    tasksForm,
    agentForm,
    convertExistingFiles,
  ]);

  const addFile = (file: File) => {
    const newFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    };
    setUploadedFiles((prev) => [...prev, newFile]);
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  /**
   * Uploads the newly attached files. Resolves `false` when the upload failed;
   * the user has already been told, so the caller stops without a message of
   * its own.
   */
  const uploadNewFiles = async (
    solutionVersionId: string
  ): Promise<boolean> => {
    const newFiles = uploadedFiles.filter((file) => file.file);

    if (newFiles.length === 0) {
      return true;
    }

    try {
      await postSolutionVersionFiles(
        solutionVersionId,
        newFiles.flatMap((file) => (file.file ? [file.file] : []))
      );

      // After successful upload, refetch the solution data to get updated files
      if (draftVersionSolutionData?.id) {
        try {
          // Use the new refetchSolutionData event to get fresh data
          sendProStudioEvent({
            type: 'refetchSolutionData',
            payload: {
              solutionVersionId: draftVersionSolutionData.id,
              cb: () => {},
            },
          });
        } catch (refetchError) {
          console.error('Error refetching solution data:', refetchError);
          sendUserMessage({
            type: 'error',
            text: 'Files uploaded but failed to refresh data',
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      // The one message the user sees. The backend's refusal text is not
      // shown: it is another service's wording and can name its internals.
      console.error('Solution file upload failed:', error);
      sendUserMessage({
        type: 'error',
        text: 'Something went wrong with files uploading',
        autoClose: 3000,
      });
      return false;
    }
    return true;
  };

  const resetFormState = () => {
    const newDefaultValues = getDefaultValues();
    configForm.reset(newDefaultValues.config);
    tasksForm.reset(newDefaultValues.tasks);
    agentForm.reset(newDefaultValues.agent);

    const existingFiles = convertExistingFiles();
    setUploadedFiles(existingFiles);
  };

  // A Save still running: a second click (or a second Save of the panel
  // before the solution reloads) would upload the same documents again.
  const savingRef = useRef(false);

  const saveSupervisor = async (callback?: () => void) => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      await saveSupervisorOnce(callback);
    } finally {
      savingRef.current = false;
    }
  };

  const saveSupervisorOnce = async (callback?: () => void) => {
    const configData = configForm.getValues();
    const tasksData = tasksForm.getValues();
    const agentData = agentForm.getValues();

    const originalTasks = draftVersionSolutionData?.tasks || [];
    const currentTaskIds = tasksData.tasks
      .filter((task) => task.taskId)
      .map((task) => task.taskId);

    const deletedTaskIds = originalTasks
      .filter((task) => task.id && !currentTaskIds.includes(task.id))
      .map((task) => task.id);

    // Process tasks: new tasks without ID, existing tasks with ID
    const processedTasks = tasksData.tasks.map((task) => {
      if (task.taskId) {
        // Existing task - include ID for update
        return {
          id: task.taskId,
          name: task.name,
          description: task.description,
        };
      } else {
        // New task - no ID for creation
        return {
          name: task.name,
          description: task.description,
        };
      }
    });

    try {
      // A knowledge base added to a solution whose knowledge search was
      // skipped: put the search back first, saved, so the backend has a RAG
      // node to point at the corpus it provisions for these files.
      // Gated on the same flag pair as the skip itself: with both off the
      // skip never ran for this user, and a record left by an earlier session
      // is not restored (candidate behaviour: this form did nothing here).
      const addingKnowledge =
        tableStore.knowledgeSearchToggleEnabled &&
        uploadedFiles.some((file) => file.file);
      let restoredSearch = false;
      if (addingKnowledge && tableStore.knowledgeSearchRestorable) {
        const restored = await tableStore.restoreKnowledgeSearch();
        restoredSearch = !!restored?.restoredNodeNumbers.length;
        restored?.notes.forEach((text) =>
          sendUserMessage({ type: 'info', text, autoClose: 6000 })
        );
        await tableStore.waitForPendingSaves();
      }

      // First, upload new files if any. Once sent they leave the list: the
      // server copies come back with the solution's reload, and until then a
      // later Save must not send them again.
      if (draftVersionSolutionData?.id) {
        if (!(await uploadNewFiles(draftVersionSolutionData.id))) {
          // No knowledge base came of it: the search restored above for these
          // files would wait on a corpus that never arrives and block Publish.
          // Skip it again, so it can be restored on the next try.
          if (restoredSearch) {
            await tableStore.applySkipKnowledgeCorpus();
            await tableStore.waitForPendingSaves();
          }
          return;
        }
        setUploadedFiles((prev) => prev.filter((file) => !file.file));
      }
      if (addingKnowledge && tableStore.knowledgeSearchUnprovisioned) {
        // Remember when these files went up, so a search index that never
        // appears can be told apart from one still being built.
        tableStore.markKnowledgeUpload();
      }
      if (restoredSearch && tableStore.knowledgeSearchUnprovisioned) {
        tableStore.watchForLiveCorpusId();
      }

      // Process files: only include existing files that weren't removed
      const existingFiles = uploadedFiles
        .filter((file) => file.serverId)
        .map((file) => {
          const originalFileData = draftVersionSolutionData?.files?.find(
            (originalFile) => originalFile.id === file.serverId
          );

          return {
            url: originalFileData?.url || '',
            mimeType: file.type,
            originalName: file.name,
            publicId: originalFileData?.publicId || '',
            size: file.size,
          };
        });

      // `knowledgeAgentContext` is sent only while its flag is allowed; the
      // other agent fields (brandName, tone) are always sent.
      const { knowledgeAgentContext, ...agentFields } = agentData;
      const version = {
        ...configData,
        tasks: processedTasks,
        files: existingFiles,
        ...agentFields,
        ...(globalInstructionsOn ? { knowledgeAgentContext } : {}),
      };

      sendProStudioEvent({
        type: 'updateSolution',
        payload: {
          version,
          cb: (updatedSolutionData) => {
            // Track newly created task IDs to avoid syncing them twice
            const newlyCreatedTaskIds = new Set<string>();

            if (updatedSolutionData?.tasks) {
              const newTasks = updatedSolutionData.tasks.filter(
                (task) =>
                  task.id &&
                  !tasksData.tasks.find(
                    (existingTask) => existingTask.taskId === task.id
                  )
              );

              // PSTUDIO-4083: collected first, created as ONE numbering
              // gesture. Saving the panel with several new tasks is a single
              // user action, so their node numbers must be one unbroken run
              // (rule 1.2) — calling the single-node path in this loop
              // allocated each number independently and could leave a hole.
              const nodesToCreate: Array<{
                id: string;
                name: string;
                description?: string;
              }> = [];
              newTasks.forEach((newTask) => {
                if (newTask.id) {
                  const taskIndex = tasksData.tasks.findIndex(
                    (task) => task.name === newTask.name && !task.taskId
                  );
                  if (taskIndex !== -1) {
                    tasksForm.setValue(`tasks.${taskIndex}.taskId`, newTask.id);

                    nodesToCreate.push({
                      id: newTask.id,
                      name: newTask.name || '',
                      description: newTask.description || '',
                    });

                    // Track this ID to prevent duplicate sync
                    newlyCreatedTaskIds.add(newTask.id);
                  }
                }
              });
              tableStore.createTaskNodesWithIds(nodesToCreate);
              // Project Bobcat: each new task becomes its own agent, as
              // Create's do, so the canvas shows it without a reload (the
              // store checks the master flag).
              if (nodesToCreate.length) {
                tableStore.applyUseCaseAgentGrouping(
                  nodesToCreate.map((t) => t.id)
                );
              }
            }

            deletedTaskIds.forEach((taskId) => {
              if (taskId) {
                console.log('Removing node for deleted task:', taskId);
                tableStore.deleteTaskNode(taskId);
              }
            });

            // Sync existing tasks, but exclude tasks we just created above
            // to prevent duplicate node creation
            if (updatedSolutionData?.tasks) {
              const tasksWithIds = updatedSolutionData.tasks
                .filter((task) => task.id && !newlyCreatedTaskIds.has(task.id))
                .map((task) => ({
                  id: task.id!,
                  name: task.name || '',
                  description: task.description || '',
                }));

              if (tasksWithIds.length > 0) {
                tableStore.syncTasksWithNodes(tasksWithIds);
              }
            }

            tableStore.updateSupervisorConfigRow({
              brandName: agentData.brandName || '',
              supervisorName: configData.supervisorName || '',
              knowledgeAgentTone: agentData.tone || '',
            });

            callback?.();
          },
        },
      });
    } catch (error) {
      console.error('Error saving supervisor:', error);
      sendUserMessage({
        type: 'error',
        text: 'Failed to save supervisor configuration',
        autoClose: 3000,
      });
    }
  };

  return {
    configForm,
    tasksForm,
    agentForm,
    uploadedFiles,
    addFile,
    removeFile,
    resetFormState,
    saveSupervisor,
    isLoading,
    setIsLoading,
  };
};
